import { useLazyQuery, useQuery } from '@apollo/client';
import previewSrc from '@fanconnect/neo-client/dist/show.html?url';
import { Box, CircularProgress, MenuItem, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSelect } from '~/components/forms/details';
import { DetailKey } from '~/components/info-pane';
import { StoppablePreview } from '~/components/preview';
import { ViewSelect } from '~/components/shows/view-select';
import { useAppContext } from '~/contexts';
import { usePreviewControl } from '~/hooks/preview';
import { showPreviewProps } from '~/lib/preview';
import type { DevicePreviewPage__Device as Device } from '../../queries/preview.generated';
import { DevicePreviewPageShowsDocument } from '../../queries/preview.generated';
import { DevicePreviewDocument } from '../../queries/queries.generated';

interface PreviewState {
  showId: number | undefined;
  viewId: number | undefined;
}

interface FullPreviewProps {
  device: Device;
}

export const FullPreview = ({ device }: FullPreviewProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();
  const [{ showId, viewId }, setPreviewState] = useState<PreviewState>({
    showId: device.activeShow?.show.id,
    viewId: device.currentView?.id,
  });

  const [loadShows, { data: showsData, loading: showsLoading, called: showsCalled }] = useLazyQuery(
    DevicePreviewPageShowsDocument,
    {
      variables: { networkId: currentNetwork.id },
    },
  );

  const availableShows = showsData?.network?.allShows ?? [];

  const { data: showPreview } = useQuery(DevicePreviewDocument, {
    skip: !showId,
    variables: { networkId: currentNetwork.id, showId: showId as number },
  });

  const currentShow = showPreview?.network?.show;
  const previewProps = useMemo(
    () => showPreviewProps(currentShow, viewId ?? '', device),
    [device, currentShow, viewId],
  );

  const previewControl = usePreviewControl('DevicePreview');

  const handleViewChange = useCallback((viewId: number | undefined) => {
    setPreviewState((current) => ({ ...current, viewId }));
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '16px' }}>
      <Box sx={{ display: 'flex', width: '100%', gap: '16px' }}>
        <Box sx={{ width: '50%' }}>
          <DetailKey as="div" id="device-detail-show">
            <Typography variant="overline">{t('pages.devices.show.preview.show_label')}</Typography>
          </DetailKey>

          <DetailSelect
            labelId="device-detail-show"
            displayEmpty
            disabled={false}
            name="showId"
            onOpen={() => {
              if (!showsCalled) {
                void loadShows();
              }
            }}
            renderValue={() => currentShow?.name ?? t('common.labels.none')}
            value={showId ?? ''}
            onChange={(event) =>
              setPreviewState((current) => ({
                ...current,
                ...(typeof event.target.value === 'number' && { showId: event.target.value }),
              }))
            }
            sx={{ fontSize: '1.2rem' }}
          >
            {showsLoading ? (
              <MenuItem disabled>
                <CircularProgress aria-hidden size={20} sx={{ mr: 1 }} />
                {t('common.status.loading')}
              </MenuItem>
            ) : (
              availableShows.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))
            )}
          </DetailSelect>
        </Box>

        <Box sx={{ width: '50%' }}>
          <DetailKey as="div" id="device-detail-view">
            <Typography variant="overline">{t('pages.devices.show.preview.view_label')}</Typography>
          </DetailKey>

          <ViewSelect
            labelId="device-detail-view"
            fullWidth
            onChange={handleViewChange}
            show={currentShow}
            showThumbnail
            value={viewId}
          />
        </Box>
      </Box>
      <Box>
        <StoppablePreview previewControl={previewControl} src={previewSrc} {...previewProps} />
      </Box>
    </Box>
  );
};
